import { gql, useMutation } from '@apollo/client';
import { Modal, useToast } from '@chakra-ui/react';
import * as React from 'react';

import { UserCsvUploadOperator } from '@/graphql-types/globalTypes';
import { useUploadFileToS3 } from '@/hooks/useUploadFileToS3';
import { logError } from '@/imports/logging/ClientLogger';
import {
  MembersCsvDeactivateModal_ExecuteUserCsvUpload,
  MembersCsvDeactivateModal_ExecuteUserCsvUploadVariables,
} from '@/imports/ui/admin/components/people/graphql-types/MembersCsvDeactivateModal_ExecuteUserCsvUpload';
import { CsvImportModalFrameEnhanced } from '@/imports/ui/components/csv/CsvImportModalFrameEnhanced';

import { FieldValidationError } from '../../../../../client/legacy-shared-modules/csv/csv-field-mapping';
import {
  ValidationFn,
  getValidateCsvFileHeaders,
  normalizeHeader,
} from '../../../../../client/legacy-shared-modules/csv/helpers';
import { SuccessModal } from '../SuccessModal';
import { usePrepareUserCsv } from './usePrepareUserCsv';
import {
  getUserDeactivateUploadTemplate,
  UserCsvHeaders,
} from './util';

const BULK_ARCHIVE_MATCH_FIELD = 'emails';

const EXECUTE_USER_CSV_UPLOAD = gql`
  mutation MembersCsvDeactivateModal_ExecuteUserCsvUpload(
    $input: ExecuteUserCsvUploadInput!
  ) {
    executeUserCsvUpload(input: $input) {
      csvUploadId
    }
  }
`;

type IMembersCsvDeactivateModalProps = {
  open: boolean;
  onClose: () => void;
};

export const MembersCsvDeactivateModal: React.FC<
  IMembersCsvDeactivateModalProps
> = (props) => {
  const [uploadCsvData, { loading: uploadMutationLoading }] = useMutation<
    MembersCsvDeactivateModal_ExecuteUserCsvUpload,
    MembersCsvDeactivateModal_ExecuteUserCsvUploadVariables
  >(EXECUTE_USER_CSV_UPLOAD);

  const [csvFile, setCsvFile] = React.useState<File | null>(null);
  const [validationErrors, setValidationErrors] =
    React.useState<FieldValidationError | null>(null);
  const [showSuccessModal, setShowSuccessModal] =
    React.useState<boolean>(false);
  const [uploadedCsv, setUploadedCsv] = React.useState<boolean>(false);
  const toast = useToast();

  const {
    prepareUserCsv,
    prepareUserCsvData,
    prepareUserCsvLoading,
    clearCsvResult,
  } = usePrepareUserCsv();

  const [uploadFileToS3, { isUploading, error: uploadError, isCanceled }] =
    useUploadFileToS3();

  const prepUserCsvUpload = prepareUserCsvData?.prepUserCsvUpload;

  const handleUploadFileToS3 = React.useCallback(
    async (file: File): Promise<void> => {
      try {
        if (
          prepUserCsvUpload?.metadataHeaders &&
          prepUserCsvUpload?.presignedS3Url
        ) {
          const newMetadataHeaders = prepUserCsvUpload.metadataHeaders.reduce(
            (obj: Record<string, string>, { name, value }) => (
              (obj[name] = value), obj
            ),
            {}
          );

          await uploadFileToS3({
            file,
            presignedS3Url: prepUserCsvUpload.presignedS3Url,
            metadataHeaders: newMetadataHeaders,
          });
        } else {
          logError(new Error("Can't upload csv file with failed prep"));
        }
      } catch (err) {
        toast({
          title: 'Failed to upload csv. Please try again.',
          status: 'error',
        });
        logError(err);
      }
    },
    [toast, uploadFileToS3, prepUserCsvUpload]
  );

  React.useEffect(() => {
    if (prepareUserCsvData && !uploadedCsv && !!csvFile) {
      setUploadedCsv(true);
      handleUploadFileToS3(csvFile).catch((err) => logError(err));
    }
  }, [
    prepareUserCsvData,
    setUploadedCsv,
    handleUploadFileToS3,
    csvFile,
    uploadedCsv,
  ]);

  const handleExecuteUserCsvUpload = async (
    csvUploadId: string,
    matchField: string
  ): Promise<void> => {
    try {
      await uploadCsvData({
        variables: {
          input: {
            csvUploadId,
            operator: UserCsvUploadOperator.DEACTIVATE,
            matchField,
          },
        },
      });
      setShowSuccessModal(true);
    } catch (err) {
      toast({
        title: 'Failed to execute user csv deactivate. Please try again.',
        status: 'error',
      });
      logError(err);
    }
  };

  const getValidateFileFn = (): ((file: File) => Promise<void>) => {
    const validateHeaders: ValidationFn = (headers) => {
      if (!headers.includes(normalizeHeader(UserCsvHeaders.EMAIL))) {
        return { errors: [`Missing match field: ${UserCsvHeaders.EMAIL}`] };
      }

      return null;
    };
    return async (file) => {
      await getValidateCsvFileHeaders({
        validateHeaders,
        onSuccess: (successFile) => {
          setValidationErrors(null);
          setCsvFile(successFile);
        },
        onErrors: (errors) => {
          setCsvFile(null);
          setValidationErrors(errors);
        },
      })(file);
    };
  };

  const onClose = (): void => {
    setValidationErrors(null);
    setCsvFile(null);
    setUploadedCsv(false);
    clearCsvResult();
    props.onClose();
  };

  const handleRemoveFile = (e: React.SyntheticEvent): void => {
    e.stopPropagation();
    setValidationErrors(null);
    setCsvFile(null);
    setUploadedCsv(false);
    clearCsvResult();
  };

  const submitFileUpload = async (csvUploadId: string): Promise<void> => {
    await handleExecuteUserCsvUpload(csvUploadId, BULK_ARCHIVE_MATCH_FIELD);
    onClose();
  };

  const onFileChange = async (file: File | null): Promise<void> => {
    clearCsvResult();
    await prepareUserCsv();
    if (!!file) {
      await getValidateFileFn()(file);
    }
  };

  const hasLoadingSideEffect =
    isUploading || prepareUserCsvLoading || uploadMutationLoading;

  const isDisabled =
    isCanceled ||
    !!uploadError ||
    hasLoadingSideEffect ||
    !!(validationErrors && validationErrors.errors) ||
    !csvFile;

  if (showSuccessModal) {
    return (
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Your Bulk Archive is in Progress"
        description="Depending on the size of your file, it might take some time to process."
      />
    );
  }

  return (
    <Modal isOpen={props.open} onClose={onClose} size="xl">
      <CsvImportModalFrameEnhanced
        pluralEntityName="Individuals"
        modalTitle="Bulk Archive Individuals"
        note="Upload a CSV with an Email column. Matching contacts will be archived."
        footerProps={{
          primaryAction: {
            onClick: async () => {  
              if (prepareUserCsvData?.prepUserCsvUpload.csvUploadId) {
                await submitFileUpload(
                  prepareUserCsvData.prepUserCsvUpload.csvUploadId
                );
              } else {
                logError(new Error('missing csvUploadId'));
              }
            },
            content: 'Archive Contacts',
            loading: hasLoadingSideEffect,
            disabled: isDisabled,
          },
        }}
        template={getUserDeactivateUploadTemplate()}
        file={csvFile}
        onFileChange={onFileChange}
        onRemoveFile={handleRemoveFile}
        validationErrors={validationErrors}
        hasLoadingSideEffect={hasLoadingSideEffect}
      />
    </Modal>
  );
};
